import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form, Field, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { events, formatEventDisplayDate, getEventSortDate } from '../events/Events';
import './RSVP.css';
import { EVENT_PRICES } from '../../config/paypal';
import { priceForType, calculateEventTotal } from '../../utils/pricing';
import { loadMemberEmails, isMemberEmail } from '../../utils/memberUtils';
import { sendRSVPEmail } from '../../utils/emailUtils';
import { fetchEventStatus, incrementRSVP } from '../../services/rsvpService';
import { loadEventStatusesFromStorage, upsertEventStatusInStorage, getRsvpClosedReason } from '../../utils/eventStatusUtils';

// Validation schema
const rsvpSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  dietary: Yup.string(),
  ticketType: Yup.string().oneOf(['member', 'nonMember']).required('Ticket type is required'),
  guestCount: Yup.number().min(1).max(5).required('Guest count is required'),
  guests: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Guest name is required'),
      dietary: Yup.string(),
      type: Yup.string().oneOf(['member', 'nonMember']).required('Guest ticket type is required')
    })
  )
});

// Custom hook to handle member status and guest updates
function useRSVPFormEffects(isLoading) {
  const { values, setFieldValue } = useFormikContext();
  const [isHCHSMember, setIsHCHSMember] = useState(false);
  const lastEmailAppliedRef = useRef('');

  // Update member status when email changes (avoid unnecessary setFieldValue)
  useEffect(() => {
    if (!values.email || isLoading) return;
    const member = isMemberEmail(values.email);
    setIsHCHSMember(member);
    const desired = member ? 'member' : 'nonMember';
    if (values.email !== lastEmailAppliedRef.current) {
      setFieldValue('ticketType', desired);
      lastEmailAppliedRef.current = values.email;
    }
  }, [values.email, isLoading, setFieldValue]);

  // Update guests array when guestCount changes (avoid infinite loops)
  useEffect(() => {
    const currentGuests = values.guests || [];
    const targetLength = Math.max(0, Number(values.guestCount || 1) - 1);
    if (currentGuests.length === targetLength) return;
    const newGuests = Array.from({ length: targetLength }, (_, i) => (
      currentGuests[i] || { name: '', dietary: '', type: isHCHSMember ? 'member' : 'nonMember' }
    ));
    setFieldValue('guests', newGuests);
  }, [values.guestCount, values.guests, isHCHSMember, setFieldValue]);

  return isHCHSMember;
}

// Calculate total using the same per-person logic as the UI/PayPal button
function calculateTotalFromValues(values) {
  return calculateEventTotal(values, isMemberEmail(values.email));
}

function RSVPForm({ event, onPaymentSuccess, onPayLater, isProcessing, setError, isTestMode }) {
  const { values, errors, touched } = useFormikContext();
  const isHCHSMember = useRSVPFormEffects(false);

  const showMemberMismatch = isHCHSMember && String(values.ticketType) === 'nonMember';

  const calculateTotal = () => calculateEventTotal(values, isHCHSMember);

  return (
    <Form className="rsvp-form">
      <div className="form-section">
        <h3>Your Information</h3>
        <p className="member-notice">
          If you are a member, be sure to use the email address you are registered with to receive the member discount.
        </p>
        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <Field
            type="text"
            id="name"
            name="name"
          />
          {errors.name && touched.name && (
            <div className="error">{errors.name}</div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="dietary">Dietary Restrictions</label>
          <Field
            type="text"
            id="dietary"
            name="dietary"
          />
          {errors.dietary && touched.dietary && (
            <div className="error">{errors.dietary}</div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <Field
            type="email"
            id="email"
            name="email"
          />
          {errors.email && touched.email && (
            <div className="error">{errors.email}</div>
          )}
          {values.email && (
            <p className="member-status">
              {isHCHSMember ? (
                <span className="member">✓ Member Discount Applied</span>
              ) : (
                <span className="non-member">Email address not associated with a current member. Contact us if you believe this is an error.</span>
              )}
            </p>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="ticketType">Ticket Type</label>
          <Field as="select" id="ticketType" name="ticketType">
            <option value="member">Member (${EVENT_PRICES.member})</option>
            <option value="nonMember">Non-member (${EVENT_PRICES.nonMember})</option>
          </Field>
          {showMemberMismatch && (
            <p className="member-warning">We recognize your email as a current member. You selected Non-member pricing; if this is unintentional, switch to Member to receive the discount.</p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="guestCount">Total number attending (including yourself)</label>
          <Field
            as="select"
            id="guestCount"
            name="guestCount"
          >
            {[1,2,3,4,5].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </Field>
        </div>
      </div>

      {Number(values.guestCount) > 1 && (
        <div className="form-section">
          <h3>Guest Information</h3>
          {values.guests.map((guest, index) => (
            <div key={index} className="guest-info">
              <h4>Guest {index + 1}</h4>
              <div className="form-group">
                <label>Name</label>
                <Field
                  type="text"
                  name={`guests.${index}.name`}
                />
                {errors.guests?.[index]?.name && touched.guests?.[index]?.name && (
                  <div className="error">{errors.guests[index].name}</div>
                )}
              </div>
              <div className="form-group">
                <label>Ticket Type</label>
                <Field as="select" name={`guests.${index}.type`}>
                  <option value="member">Member (${EVENT_PRICES.member})</option>
                  <option value="nonMember">Non-member (${EVENT_PRICES.nonMember})</option>
                </Field>
              </div>
              <div className="form-group">
                <label>Dietary Restrictions</label>
                <Field
                  type="text"
                  name={`guests.${index}.dietary`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="payment-summary">
        <h3>Payment Summary</h3>
        <div className="price-breakdown">
          <p className="price-type">
            {isTestMode ? (
              <span className="test-price">Test Mode Price: $0.01</span>
            ) : (
              <>
                Selected Prices — Primary: ${priceForType(values.ticketType || (isHCHSMember ? 'member' : 'nonMember'))}
              </>
            )}
          </p>
          {!isTestMode && values.guests.length > 0 && (
            <p>Guests total: ${(values.guests || []).reduce((sum, g) => sum + priceForType(g.type || 'nonMember'), 0)}</p>
          )}
          <p className="total">Total: ${calculateTotal().toFixed(2)}</p>
        </div>

        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label htmlFor="paymentMethod">Payment Method</label>
          <Field as="select" id="paymentMethod" name="paymentMethod">
            <option value="paypal">PayPal (pay now)</option>
            <option value="payLater">Pay later / in person</option>
          </Field>
        </div>

        <div className="paypal-container">
          {Object.keys(errors).length > 0 ? (
            <div className="validation-message">
              <p>Please complete all required fields before proceeding to payment.</p>
              <ul>
                {Object.entries(errors).map(([field, error]) => {
                  if (typeof error === 'object') {
                    if (Array.isArray(error)) {
                      return error.map((err, index) => (
                        err && typeof err === 'object' ? (
                          Object.entries(err).map(([subField, subError]) => (
                            <li key={`${field}-${index}-${subField}`}>{subError}</li>
                          ))
                        ) : (
                          <li key={`${field}-${index}`}>{err}</li>
                        )
                      ));
                    } else {
                      return Object.entries(error).map(([subField, subError]) => (
                        <li key={`${field}-${subField}`}>{subError}</li>
                      ));
                    }
                  }
                  return <li key={field}>{error}</li>;
                })}
              </ul>
            </div>
          ) : (
            <PayPalScriptProvider options={{
              "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
              currency: "USD",
              intent: "capture"
            }}>
              {values.paymentMethod === 'paypal' ? (
              <PayPalButtons
                style={{
                  layout: "vertical",
                  shape: "rect",
                  height: 55
                }}
                createOrder={(data, actions) => {
                  return actions.order.create({
                    purchase_units: [{
                      amount: {
                        value: calculateTotal().toFixed(2),
                        currency_code: "USD"
                      },
                      description: `HCHS dinner ${formatEventDisplayDate(event)}${isTestMode ? ' [TEST]' : ''}`
                    }]
                  });
                }}
                onApprove={async (data, actions) => {
                  await onPaymentSuccess(values, actions);
                }}
                onError={(err) => {
                  console.error('PayPal error:', err);
                  setError('There was an error processing your payment. Please try again.');
                }}
              />
              ) : (
                <button
                  type="button"
                  className="button"
                  onClick={async () => { await onPayLater(values); }}
                  disabled={isProcessing}
                >
                  Confirm RSVP (Pay later)
                </button>
              )}
            </PayPalScriptProvider>
          )}
          {isProcessing && <div className="processing">Processing payment...</div>}
        </div>
      </div>
    </Form>
  );
}

function RSVP() {
  const { eventSlug } = useParams();
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [status, setStatus] = useState(() => {
    try {
      const map = loadEventStatusesFromStorage();
      return map && eventSlug && map[eventSlug] ? map[eventSlug] : null;
    } catch (_) {
      return null;
    }
  });
  // 'past' | 'closed' | 'full' | null — gates the form below.
  const closedReason = getRsvpClosedReason(event, event ? getEventSortDate(event) : null, status);

  useEffect(() => {
    loadMemberEmails().then(() => {
      setIsLoading(false);
    });

    const foundEvent = events.find(e => e.rsvpLink === `/rsvp/${eventSlug}`);
    setEvent(foundEvent);
    fetchEventStatus(eventSlug).then((data) => {
      if (data) {
        setStatus(data);
        upsertEventStatusInStorage(eventSlug, data);
      }
    });
  }, [eventSlug]);

  const handlePaymentSuccess = async (values, actions) => {
    setIsProcessing(true);
    try {
      const order = await actions.order.capture();
      const total = calculateTotalFromValues(values);
      
      const emailSent = await sendRSVPEmail({
        name: values.name,
        email: values.email,
        primaryDietary: values.dietary,
        primaryTicketType: values.ticketType,
        eventTitle: event.title,
        eventDate: formatEventDisplayDate(event),
        guestCount: values.guestCount,
        isHCHSMember: isMemberEmail(values.email),
        guests: values.guests,
        total: total,
        paymentDetails: {
          ...order,
          timestamp: new Date().toISOString(),
          isTestMode: false
        }
      });

      const attendees = [values.name, ...(values.guests || []).map(g => g.name).filter(Boolean)];
      const times = event?.times || {};
      if (emailSent) {
        setRsvpStatus({
          success: true,
          message: 'Your RSVP has been confirmed! A confirmation email has been sent.',
          orderId: order.id,
          timestamp: new Date().toISOString(),
          isTestMode: false,
          amount: total,
          eventTitle: event?.title,
          eventDate: formatEventDisplayDate(event),
          eventTimes: times,
          attendees
        });
      } else {
        setRsvpStatus({
          success: true,
          message: 'Your payment was successful, but there was an error sending the confirmation email. Please contact us to confirm your RSVP.',
          orderId: order.id,
          timestamp: new Date().toISOString(),
          isTestMode: false,
          amount: total,
          eventTitle: event?.title,
          eventDate: formatEventDisplayDate(event),
          eventTimes: times,
          attendees
        });
      }

      // Increment RSVP count online (best-effort)
      const inc = await incrementRSVP(eventSlug, values.guestCount);
      if (inc && !inc.error) {
        setStatus(prev => {
          const base = prev || { capacity: null, sold: 0 };
          const newSold = inc.sold;
          const newRemaining = (base.capacity != null) ? Math.max(0, base.capacity - newSold) : null;
          const updated = { ...base, sold: newSold, remaining: newRemaining };
          upsertEventStatusInStorage(eventSlug, updated);
          return updated;
        });
      } else if (inc && inc.error) {
        setError('The event just reached capacity. Please contact us.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('There was an error processing your payment. Please try again or contact us for assistance.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayLater = async (values) => {
    setIsProcessing(true);
    try {
      const total = calculateTotalFromValues(values);
      const emailSent = await sendRSVPEmail({
        name: values.name,
        email: values.email,
        primaryDietary: values.dietary,
        primaryTicketType: values.ticketType,
        eventTitle: event.title,
        eventDate: formatEventDisplayDate(event),
        guestCount: values.guestCount,
        isHCHSMember: isMemberEmail(values.email),
        guests: values.guests,
        total: total,
        paymentDetails: {
          id: 'PAY-LATER-' + Date.now(),
          status: 'PAY_LATER',
          timestamp: new Date().toISOString(),
          isTestMode: false
        }
      });

      // Always confirm the reservation. If the notification email failed we must
      // still say so — otherwise the page sits silent and neither the guest nor
      // HCHS learns that the RSVP was recorded.
      setRsvpStatus({
        success: true,
        message: emailSent
          ? 'Your RSVP has been recorded. Please pay in person at the event.'
          : 'Your RSVP has been recorded, but we were unable to send your confirmation email. Please contact us at hchs1923@gmail.com or 832.428.5252 so we can confirm your reservation.',
        orderId: null,
        timestamp: new Date().toISOString(),
        isTestMode: false,
        amount: total,
        eventTitle: event?.title,
        eventDate: formatEventDisplayDate(event),
        eventTimes: event?.times || {},
        attendees: [values.name, ...(values.guests || []).map(g => g.name).filter(Boolean)]
      });

      const inc = await incrementRSVP(eventSlug, values.guestCount);
      if (inc && !inc.error) {
        setStatus(prev => {
          const base = prev || { capacity: null, sold: 0 };
          const newSold = inc.sold;
          const newRemaining = (base.capacity != null) ? Math.max(0, base.capacity - newSold) : null;
          const updated = { ...base, sold: newSold, remaining: newRemaining };
          upsertEventStatusInStorage(eventSlug, updated);
          return updated;
        });
      } else if (inc && inc.error) {
        setError('The event just reached capacity. Please contact us.');
      }
    } catch (err) {
      console.error('Error processing pay-later RSVP:', err);
      setError('There was an error recording your RSVP. Please try again or contact us.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!event || isLoading) return <div>Loading...</div>;

  return (
    <div className="rsvp-page">
      <section className="page-hero">
        <h1>RSVP for Event</h1>
        <p>{event.title}</p>
      </section>

      <div className="rsvp-container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {rsvpStatus && (
          <div className={`status-message ${rsvpStatus.success ? 'success' : 'error'}`}>
            <h3>{rsvpStatus.success ? 'RSVP Confirmed!' : 'RSVP Error'}</h3>
            <p>{rsvpStatus.message}</p>
            {rsvpStatus.success && (
              <div className="event-confirmation-details">
                <p><strong>Event:</strong> {rsvpStatus.eventTitle}</p>
                <p><strong>Date:</strong> {rsvpStatus.eventDate}</p>
                {rsvpStatus.eventTimes && (
                  <p className="time"><strong>Times:</strong> Social {rsvpStatus.eventTimes.social} • Dinner {rsvpStatus.eventTimes.dinner} • Program {rsvpStatus.eventTimes.program}</p>
                )}
                {Array.isArray(rsvpStatus.attendees) && rsvpStatus.attendees.length > 0 && (
                  <div>
                    <p><strong>Attendees:</strong></p>
                    <ul>
                      {rsvpStatus.attendees.map((n, i) => (
                        <li key={`${n}-${i}`}>{n}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p>You will receive an email RSVP confirmation.</p>
              </div>
            )}
            {rsvpStatus.orderId && (
              <div className="order-details">
                <p>Order ID: {rsvpStatus.orderId}</p>
            <p>Amount Paid: ${rsvpStatus.isTestMode ? '0.01' : (rsvpStatus.amount != null ? rsvpStatus.amount.toFixed(2) : '0.00')}</p>
                <p>Confirmed: {new Date(rsvpStatus.timestamp).toLocaleString()}</p>
              </div>
            )}
          </div>
        )}

        {!rsvpStatus && (
          <>
            <div className="event-summary">
              <h2>Event Details</h2>
              <p className="date">{formatEventDisplayDate(event)}</p>
              <p className="time">
                Social Time: {event.times.social} • 
                Dinner: {event.times.dinner} • 
                Program: {event.times.program}
              </p>
              {event.location && <p className="location">{event.location}</p>}
              <p className="deadline">{event.isClosed ? 'RSVPs are closed for this event.' : event.rsvpDeadline}</p>
              {/* Capacity count intentionally removed for cleaner UI */}
            </div>

            <Formik
              initialValues={{
                name: '',
                email: '',
                dietary: '',
                ticketType: '',
                guestCount: 1,
                guests: [{ name: '', dietary: '' }],
                paymentMethod: 'paypal'
              }}
              validationSchema={rsvpSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(false);
              }}
            >
              {({ values, errors, touched, handleSubmit, isSubmitting }) => (
                <div className="rsvp-form">
                  {closedReason ? (
                    <div className="status-message">
                      <h3>RSVP Closed</h3>
                      <p>
                        {closedReason === 'past'
                          ? 'This event has already taken place and is no longer accepting RSVPs.'
                          : closedReason === 'full'
                            ? 'This event is now full and no longer accepting RSVPs.'
                            : 'RSVPs are closed for this event.'}
                      </p>
                    </div>
                  ) : (
                    <RSVPForm 
                      event={event}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPayLater={async (vals) => {
                        await handlePayLater(vals);
                      }}
                      isProcessing={isProcessing}
                      setError={setError}
                      isTestMode={false}
                    />
                  )}
                </div>
              )}
            </Formik>
          </>
        )}
      </div>
    </div>
  );
}

export default RSVP; 